/*
 * HOURS-CALCULATION.JS - Hours Calculation Utilities for Project Tasks
 * 
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * 
 * SUMMARY:
 * Utility functions to calculate forecast hours and actual hours for tasks:
 * - Forecast hours: extracted from task labor field (original planned work estimate)
 * - Actual hours: calculated from approved timesheet entries only (completed work)
 * - Color styling: Uses SCSS classes for better performance and maintainability
 * 
 * MAJOR FUNCTIONS:
 * - calculateForecastHours() - Extracts planned hours from task labor field
 * - calculateActualHours() - Calculates actual hours from approved timesheet entries
 * - formatHoursDisplay() - Formats hours for display (e.g., "80 hrs")
 * - aggregateHoursByStage() - Aggregates hours at stage/section level
 * - getActualHoursColorClass() - Returns CSS class based on actual hours thresholds
 * 
 * FEATURES:
 * - Task-level forecast hours from task labor fields
 * - Task-level actual hours from approved timesheet entries
 * - Stage-level aggregation combining both forecast and actual
 * - Clear distinction between planned (forecast) and completed (actual) work
 * - Formatted display strings with consistent "X hrs" format
 * - Color-coded actual hours display via SCSS classes:
 *   • 0-79 hours: normal text (hours-normal)
 *   • 80-100 hours: green text with medium weight (hours-moderate)  
 *   • 101+ hours: red text with bold weight (hours-high)
 * - Backward compatibility with deprecated inline style function
 */

import moment from 'moment';
import { toDecimal, roundValue } from '../../../util/decimal-utils';

/**
 * Calculates forecast hours from task's subtasks labor quantity
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * @Updated 2025-07-03 - Changed to use subtasks labor.quantity for forecast hours
 * 
 * @description Extracts planned hours from subtasks' labor.quantity fields.
 * Each subtask has a labor JSONB field with quantity representing forecast hours.
 * 
 * @param {Object} task - The task object containing subtasks with labor fields
 * @returns {number} Total forecast hours from all subtasks' labor.quantity
 * 
 * @example
 * const task = { 
 *   subtasks: [
 *     { labor: { quantity: 8 } }, 
 *     { labor: { quantity: 4 } }
 *   ] 
 * };
 * const forecastHours = calculateForecastHours(task); // Returns 12
 */
export const calculateForecastHours = (task) => {
  if (task && task.forecastHours !== undefined && task.forecastHours !== null) {
    return Number(task.forecastHours) || 0;
  }
  if (!task || !task.subtasks || !Array.isArray(task.subtasks)) {
    return 0;
  }

  let totalForecastHours = toDecimal(0);

  task.subtasks.forEach(subtask => {
    if (subtask.labor && Array.isArray(subtask.labor)) {
      // Labor is a JSONB field, extract quantity for personnel resources only
      const quantity = subtask.labor.reduce((acc, laborItem) => {
        try {
          const laborData = typeof laborItem === 'string' ? JSON.parse(laborItem) : laborItem;
          // Include if is_personnel is true or undefined (Source of Truth)
          if (laborData.is_personnel !== false && laborData.isPersonnel !== false) {
            const qty = laborData.qty?.value !== undefined ? laborData.qty.value : (laborData.qty || laborData.quantity || 0);
            return acc.plus(toDecimal(qty));
          }
        } catch (error) {
          console.error('Error parsing labor in calculateForecastHours:', error);
        }
        return acc;
      }, toDecimal(0));
      totalForecastHours = totalForecastHours.plus(quantity);
    }
  });

  return totalForecastHours.toNumber();
};

/**
 * Calculates actual hours from timesheet entries for a specific task
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * 
 * @description Processes approved timesheet entries to calculate total actual hours
 * worked on a specific task, including overtime calculations.
 * 
 * @param {string} taskId - The ID of the task to calculate hours for
 * @param {Array} timesheetEntries - Array of timesheet entries from the project
 * @returns {number} Total actual hours worked on the task
 * 
 * @example
 * const actualHours = calculateActualHours('1a1c87d9-2a0e-44b0-a9da-3998547dd64a', timesheetEntries); // Returns 8
 */
export const calculateActualHours = (id, timesheetEntries = [], type = 'TASK', hoursMap = null) => {
  if (hoursMap) {
    const map = type === 'TASK' ? hoursMap.taskMap : hoursMap.stageMap;
    return map.get(id)?.toNumber() || 0;
  }

  let totalHours = toDecimal(0);

  timesheetEntries.forEach(entry => {
    // Determine if entry matches based on type (TASK or STAGE)
    // For STAGE type, we only count entries that have NO taskId to avoid double counting
    const isMatched = type === 'TASK' 
      ? entry.taskId === id 
      : (entry.stageId === id && !entry.taskId);

    if (entry.status === 'APPROVED' && isMatched) {
      const startTime = moment(entry.approvedStartTime || entry.startTime);
      const endTime = moment(entry.approvedEndTime || entry.endTime);

      if (startTime.isValid() && endTime.isValid()) {
        const hours = toDecimal(endTime.diff(startTime, 'hours', true));
        totalHours = totalHours.plus(hours);
      }
    }
  });

  return totalHours.toNumber();
};

/**
 * Pre-processes timesheet entries into a Map for fast lookup.
 * @param {Array} timesheetEntries - Array of timesheet entries
 * @returns {Object} { taskMap, stageMap }
 */
export const getTimesheetHoursMap = (timesheetEntries = []) => {
  const taskMap = new Map();
  const stageMap = new Map();

  timesheetEntries.forEach(entry => {
    if (entry.status === 'APPROVED') {
      const startTime = moment(entry.approvedStartTime || entry.startTime);
      const endTime = moment(entry.approvedEndTime || entry.endTime);

      if (startTime.isValid() && endTime.isValid()) {
        const hours = toDecimal(endTime.diff(startTime, 'hours', true));
        
        if (entry.taskId) {
          const current = taskMap.get(entry.taskId) || toDecimal(0);
          taskMap.set(entry.taskId, current.plus(hours));
        } else if (entry.stageId) {
          const current = stageMap.get(entry.stageId) || toDecimal(0);
          stageMap.set(entry.stageId, current.plus(hours));
        }
      }
    }
  });

  return { taskMap, stageMap };
};

/**
 * Formats hours for display in the UI
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * 
 * @description Formats numerical hours into a user-friendly display string
 * 
 * @param {number} hours - The number of hours to format
 * @returns {string} Formatted hours string (e.g., "80 hrs", "0 hrs")
 * 
 * @example
 * formatHoursDisplay(80.5); // Returns "80.5 hrs"
 * formatHoursDisplay(0); // Returns "0 hrs"
 */
export const formatHoursDisplay = (hours) => {
  const formattedHours = parseFloat(hours || 0).toFixed(hours % 1 === 0 ? 0 : 1);
  return `${formattedHours} hrs`;
};

/**
 * Aggregates hours by stage/section
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * @Modified 2025-07-28 - Corrected variance and utilisation rate calculation for stages.
 * 
 * @description Calculates total forecast and actual hours for all tasks within a stage/section.
 * Forecast hours come from task labor fields, actual hours from approved timesheet entries.
 * Then, it calculates the stage-level variance and utilisation rate based on these totals.
 * 
 * @param {Array} tasks - Array of tasks within the stage
 * @param {Array} timesheetEntries - Array of timesheet entries for the project
 * @returns {Object} Object with totalForecastHours, totalActualHours, variance, and utilisationRate
 * 
 * @example
 * const stageTotals = aggregateHoursByStage(tasks, timesheetEntries);
 * // Returns { totalForecastHours: 240, totalActualHours: 232, variance: -3.3, utilisationRate: 96.7 }
 */
export const aggregateHoursByStage = (tasks = [], timesheetEntries = [], stageId = null, hoursMap = null) => {
  let totalForecastHours = toDecimal(0);
  let totalActualHours = toDecimal(0);

  const localHoursMap = hoursMap || getTimesheetHoursMap(timesheetEntries);

  tasks.forEach(task => {
    const forecastHours = toDecimal(calculateForecastHours(task));
    const actualHours = toDecimal(calculateActualHours(task.id, timesheetEntries, 'TASK', localHoursMap));
    totalForecastHours = totalForecastHours.plus(forecastHours);
    totalActualHours = totalActualHours.plus(actualHours);
  });

  // Include hours directly attributed to the stage (where taskId is null)
  if (stageId) {
    const stageDirectHours = toDecimal(calculateActualHours(stageId, timesheetEntries, 'STAGE', localHoursMap));
    totalActualHours = totalActualHours.plus(stageDirectHours);
  }

  const forecast = totalForecastHours.toNumber();
  const actual = totalActualHours.toNumber();

  // Calculate stage-level variance and utilisation rate AFTER summing totals
  const stageVariance = getVariance(actual, forecast);
  const stageUtilisationRate = getUtilisationRate(actual, forecast);
  const stageEffortCompletionPercentage = getEffortCompletionPercentage(actual, forecast);

  return {
    totalForecastHours: forecast,
    totalActualHours: actual,
    variance: stageVariance,
    utilisationRate: stageUtilisationRate,
    effortCompletionPercentageValue: stageEffortCompletionPercentage,
    effortCompletionPercentage: `${stageEffortCompletionPercentage.toFixed(2)}%`
  };
};

/**
 * Calculates hours data for multiple tasks
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * 
 * @description Processes an array of tasks and returns hours data for each task.
 * Forecast hours from task labor fields, actual hours from approved timesheet entries.
 * 
 * @param {Array} tasks - Array of task objects
 * @param {Array} timesheetEntries - Array of timesheet entries
 * @returns {Object} Object mapping task IDs to their hours data
 * 
 * @example
 * const hoursData = calculateTasksHours(tasks, timesheetEntries);
 * // Returns { 'task-1': { forecastHours: 80, actualHours: 72 , variance: -10% , utilisationRate: 90%}, ... }
 */
export const calculateTasksHours = (tasks = [], timesheetEntries = [], hoursMap = null) => {
  const hoursData = {};
  const localHoursMap = hoursMap || getTimesheetHoursMap(timesheetEntries);

  tasks.forEach(task => {
    const forecast = calculateForecastHours(task);
    
    // Aggregate actual hours from all subtasks to match how forecast hours are calculated
    let totalActual = toDecimal(0);
    if (task.subtasks && Array.isArray(task.subtasks)) {
      task.subtasks.forEach(subtask => {
        totalActual = totalActual.plus(toDecimal(calculateActualHours(subtask.id, timesheetEntries, 'TASK', localHoursMap)));
      });
    }
    // Also include hours logged directly against the task itself (if any)
    totalActual = totalActual.plus(toDecimal(calculateActualHours(task.id, timesheetEntries, 'TASK', localHoursMap)));
    
    const actual = totalActual.toNumber();
    const taskVariance = getVariance(actual, forecast);
    const taskUtilisationRate = getUtilisationRate(actual, forecast);
    const taskEffortCompletionPercentage = getEffortCompletionPercentage(actual, forecast);

    hoursData[task.id] = {
      forecastHours: forecast,
      actualHours: actual,
      forecastHoursDisplay: formatHoursDisplay(forecast),
      actualHoursDisplay: formatHoursDisplay(actual),
      variance: roundValue(taskVariance, 0), // % over/under budget - whole numbers
      utilisationRate: roundValue(taskUtilisationRate, 0), // % of budget used - whole numbers
      effortCompletionPercentageValue: taskEffortCompletionPercentage,
      effortCompletionPercentage: `${taskEffortCompletionPercentage.toFixed(2)}%`,
    };
  });

  return hoursData;
};

/**
 * Gets the CSS class for actual hours based on variance percentage thresholds
 * @Author Samuel Paintsil
 * @Created 2025-07-01
 * @Updated 2025-12-22 - Changed from hour-based to variance-based traffic light system
 * 
 * @description Returns appropriate CSS class for actual hours display based on variance percentage.
 * Traffic Light Rules:
 *   - Green (hours-under-budget): Variance < -10% (under budget)
 *   - Orange (hours-on-budget): Variance between -10% and +10% (acceptable range)
 *   - Red (hours-over-budget): Variance > +10% (over budget)
 * 
 * @param {number} variancePercent - The variance percentage value
 * @returns {string} CSS class name for styling
 * 
 * @example
 * getActualHoursColorClass(-15); // Returns 'hours-under-budget' (green)
 * getActualHoursColorClass(5);   // Returns 'hours-on-budget' (orange)
 * getActualHoursColorClass(20);  // Returns 'hours-over-budget' (red)
 */
export const getActualHoursColorClass = (variancePercent) => {
  const variance = parseFloat(variancePercent || 0);

  if (variance === 0) {
    return 'text-secondary';       // Gray - 0 value
  } else if (variance > 10) {
    return 'hours-over-budget';    // Red - over budget
  } else if (variance < -10) {
    return 'hours-under-budget';   // Green - under budget
  } else {
    return 'hours-on-budget';      // Orange - within acceptable range
  }
};




/**
 * Calculates the variance percentage between actual and budgeted (forecast) hours for a task or stage.
 *
 * @param {number} actualHours - The actual hours spent (can be float, may be 0 or negative).
 * @param {number} budgetedHours - The planned/forecast hours (must be >0 for calculation).
 * @returns {number} Variance as a percentage, rounded to 1 decimal place. Negative means under budget, positive means over budget.
 *
 * @example
 * getVariance(120, 100); // 20.0 (20% over budget)
 * getVariance(80, 100); // -20.0 (20% under budget)
 * getVariance(100, 0); // 0 (division by zero protection)
 */
export const getVariance = (actualHours, budgetedHours) => {
  const actual = toDecimal(actualHours);
  const budgeted = toDecimal(budgetedHours);

  if (budgeted.isZero() || actual.isZero()) return 0; // Division by zero protection

  const variance = actual.minus(budgeted).dividedBy(budgeted).times(100);
  return roundValue(variance, 0); // Round to whole numbers
};

/**
 * Calculates the utilisation rate as a percentage of actual hours over budgeted (forecast) hours for a task or stage.
 *
 * @param {number} actualHours - The actual hours spent (can be float, may be 0 or negative).
 * @param {number} budgetedHours - The planned/forecast hours (must be >0 for calculation).
 * @returns {number} Utilisation as a percentage, rounded to 1 decimal place. <100% is under-utilised, >100% is over-utilised.
 *
 * @example
 * getUtilisationRate(120, 100); // 120.0 (20% over-utilised)
 * getUtilisationRate(80, 100); // 80.0 (20% under-utilised)
 * getUtilisationRate(100, 0); // 0 (division by zero protection)
 */
export const getUtilisationRate = (actualHours, budgetedHours) => {
  const actual = toDecimal(actualHours);
  const budgeted = toDecimal(budgetedHours);

  if (budgeted.isZero() || actual.isZero()) return 0; // Division by zero protection

  const utilisation = actual.dividedBy(budgeted).times(100);
  return roundValue(utilisation, 2);
};

/**
 * Calculates the completion percentage of effort as actual hours over planned hours.
 * Formula: (actual / planned) * 100
 * 
 * @param {number} actualHours - actual hours spent
 * @param {number} plannedHours - planned/forecast hours
 * @returns {number} Completion percentage of effort, rounded to 2 decimal places.
 */
export const getEffortCompletionPercentage = (actualHours, plannedHours) => {
  const actual = toDecimal(actualHours);
  const planned = toDecimal(plannedHours);

  if (planned.isZero() || actual.isZero()) return 0; // Division by zero protection

  const completion = actual.dividedBy(planned).times(100);
  return roundValue(completion, 2);
};
